import React from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useParams } from "react-router-dom";

import { AffinityForm, ConfirmationView, FormView } from "affinityForm";
import { translateErrorMessages, useDryRunMutation, useSubmitMutation } from "affinityForm/helpers";
import { Layout } from "affinityUI/structures/layout";
import {
  PatientConfirmation,
  PatientForm,
  PatientFormData,
  PatientsFormContext,
  UpdatePatientFormData,
} from "components/views/patientDetails/patientEdit";
import { patientValidationErrors } from "components/views/patientDetails/patientEdit/patientForm/messages";
import { patientValidationSchema } from "components/views/patientDetails/patientEdit/patientForm/validationSchema";
import { UpdatePatient, useGetPatientDetailsQuery } from "gql";
import { collectGarbage, composeUpdates, invalidatePatientsList } from "gql/invalidations";

const patientVarSetter = (patient: PatientFormData) => ({
  patient: patient as UpdatePatientFormData,
});

export type PreviousPatientData = NonNullable<ReturnType<typeof useGetPatientDetailsQuery>["data"]>["patient"];

export const PreviousPatientDataContext = React.createContext<PreviousPatientData>(undefined);

export const UpdatePatientPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { showBoundary: onError } = useErrorBoundary();
  const { data, loading } = useGetPatientDetailsQuery({ onError, variables: { id: id as string } });
  const patient = React.useMemo(
    () =>
      data?.patient
        ? {
            ...data?.patient,
            consent: {
              service: Boolean(data?.patient?.consent?.service),
              privacyPolicy: Boolean(data?.patient?.consent?.privacyPolicy),
              feedback: Boolean(data?.patient?.consent?.feedback),
            },
          }
        : undefined,
    [data]
  );

  const update = composeUpdates([invalidatePatientsList, collectGarbage]);

  const validatePatient = useDryRunMutation(UpdatePatient, {
    varSetter: patientVarSetter,
    dataGetter: (response) => response?.data?.updatePatient,
    parseError: translateErrorMessages(patientValidationErrors),
  });
  const updatePatient = useSubmitMutation(UpdatePatient, {
    varSetter: patientVarSetter,
    update,
    onSave: `/patients/${patient?.id}`,
    dataGetter: (response) => response?.data?.updatePatient,
  });

  return (
    <PreviousPatientDataContext.Provider value={data?.patient}>
      <AffinityForm<PatientFormData>
        initialData={patient}
        validationSchema={patientValidationSchema}
        asyncValidation={validatePatient}
        context={PatientsFormContext}
        confirmHandler={updatePatient}
      >
        <FormView>
          <Layout title="Edit patient" showBackBtn backLink=".." loading={loading}>
            <PatientForm />
          </Layout>
        </FormView>
        <ConfirmationView>
          <Layout title="Confirm patient" showBackBtn backLink=".." backLinkText="Back to edit patient">
            <PatientConfirmation />
          </Layout>
        </ConfirmationView>
      </AffinityForm>
    </PreviousPatientDataContext.Provider>
  );
};
